const DATABASE = 'ame.blog.recovery.v1'
const MATERIALS_KEY = 'blog.local.materials.v1'
const LEGACY_KEY = 'ameEdit.local.materials.v1'
let connection

export const readLegacyMaterials = () => {
  const raw = window.localStorage.getItem(MATERIALS_KEY) ?? window.localStorage.getItem(LEGACY_KEY)
  const records = JSON.parse(raw || '[]')
  if (!Array.isArray(records)) throw new Error('Invalid legacy recovery data')
  return records
}

const migrate = (db) =>
  new Promise((resolve, reject) => {
    const transaction = db.transaction(['materials', 'meta'], 'readwrite')
    const meta = transaction.objectStore('meta')
    const marker = meta.get('legacy-migrated')
    marker.onsuccess = () => {
      if (marker.result) return
      const materials = transaction.objectStore('materials')
      try {
        readLegacyMaterials().forEach(({ _localPersist, ...record }) => {
          if (record.id) materials.put(record)
        })
        meta.put(true, 'legacy-migrated')
      } catch (_error) {
        transaction.abort()
      }
    }
    transaction.oncomplete = () => {
      // Only remove the old copies after the whole migration has committed.
      try {
        window.localStorage.removeItem(MATERIALS_KEY)
        window.localStorage.removeItem(LEGACY_KEY)
      } catch (_error) {
        /* IndexedDB remains the source of recovery data. */
      }
      resolve(db)
    }
    transaction.onabort = () => reject(transaction.error || new Error('Recovery migration failed'))
  })

const openDatabase = () => {
  if (!connection) {
    connection = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DATABASE, 1)
      let blocked = false
      request.onupgradeneeded = () => {
        request.result.createObjectStore('materials', { keyPath: 'id' })
        request.result.createObjectStore('meta')
      }
      request.onerror = () => reject(request.error)
      request.onblocked = () => {
        blocked = true
        reject(new Error('Recovery database is blocked by another tab'))
      }
      request.onsuccess = () => {
        const db = request.result
        if (blocked) {
          db.close()
          return
        }
        db.onversionchange = () => {
          db.close()
          connection = null
        }
        migrate(db).then(resolve, (error) => {
          db.close()
          reject(error)
        })
      }
    }).catch((error) => {
      connection = null
      throw error
    })
  }
  return connection
}

export const readRecoveryMaterials = async () => {
  const db = await openDatabase()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('materials', 'readonly')
    const request = transaction.objectStore('materials').getAll()
    transaction.oncomplete = () => resolve(request.result)
    transaction.onabort = () => reject(transaction.error || new Error('Recovery read failed'))
  })
}

// Read/modify/write one article atomically; unrelated drafts are never rewritten.
export const updateRecoveryMaterial = async (id, update) => {
  const db = await openDatabase()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('materials', 'readwrite')
    const store = transaction.objectStore('materials')
    const request = store.get(id)
    let result
    request.onsuccess = () => {
      try {
        result = update(request.result)
        if (result) store.put(result)
        else store.delete(id)
      } catch (_error) {
        transaction.abort()
      }
    }
    transaction.oncomplete = () => resolve(result)
    transaction.onabort = () => reject(transaction.error || new Error('Recovery write failed'))
  })
}
